
import { GeometryType, RawGpxFeature } from '../types';

/**
 * Parses a GPX file and extracts distinct features (Tracks, Routes, Waypoints).
 * Each feature retains its name and coordinates.
 */
export const parseGpxFile = async (file: File): Promise<RawGpxFeature[]> => {
  return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
          try {
              const text = e.target?.result as string;
              const parser = new DOMParser();
              const xmlDoc = parser.parseFromString(text, "text/xml");
              const results: RawGpxFeature[] = [];

              // 1. Process Tracks (<trk>)
              // Tracks can have multiple segments (<trkseg>), usually we flatten them or treat them as one line.
              const tracks = Array.from(xmlDoc.getElementsByTagName("trk"));
              tracks.forEach((trk, index) => {
                  const name = trk.getElementsByTagName("name")[0]?.textContent || `Track ${index + 1}`;
                  const coords: [number, number][] = [];
                  
                  const segments = Array.from(trk.getElementsByTagName("trkseg"));
                  segments.forEach(seg => {
                      const points = Array.from(seg.getElementsByTagName("trkpt"));
                      points.forEach(pt => {
                          const lat = parseFloat(pt.getAttribute("lat") || "0");
                          const lon = parseFloat(pt.getAttribute("lon") || "0");
                          coords.push([lat, lon]);
                      });
                  });

                  if (coords.length > 0) {
                      results.push({
                          id: `trk-${index}-${Date.now()}`,
                          name: name,
                          type: 'Track',
                          coordinates: coords
                      });
                  }
              });

              // 2. Process Routes (<rte>)
              const routes = Array.from(xmlDoc.getElementsByTagName("rte"));
              routes.forEach((rte, index) => {
                  const name = rte.getElementsByTagName("name")[0]?.textContent || `Route ${index + 1}`;
                  const coords: [number, number][] = [];
                  
                  const points = Array.from(rte.getElementsByTagName("rtept"));
                  points.forEach(pt => {
                      const lat = parseFloat(pt.getAttribute("lat") || "0");
                      const lon = parseFloat(pt.getAttribute("lon") || "0");
                      coords.push([lat, lon]);
                  });

                  if (coords.length > 0) {
                      results.push({
                          id: `rte-${index}-${Date.now()}`,
                          name: name,
                          type: 'Route',
                          coordinates: coords
                      });
                  }
              });

              // 3. Process Waypoints (<wpt>)
              const waypoints = Array.from(xmlDoc.getElementsByTagName("wpt"));
              waypoints.forEach((wpt, index) => {
                  const name = wpt.getElementsByTagName("name")[0]?.textContent || `Waypoint ${index + 1}`;
                  const lat = parseFloat(wpt.getAttribute("lat") || "0");
                  const lon = parseFloat(wpt.getAttribute("lon") || "0");

                  results.push({
                      id: `wpt-${index}-${Date.now()}`,
                      name: name,
                      type: 'Waypoint',
                      coordinates: [lat, lon]
                  });
              });

              resolve(results);
          } catch (err) {
              reject(err);
          }
      };
      reader.onerror = reject;
      reader.readAsText(file);
  });
};

/**
 * Determines if a set of coordinates represents a closed Polygon or an open Polyline.
 * @param coords Array of [lat, lon] tuples
 */
export const determineGeometryType = (coords: [number, number][]): GeometryType => {
    // Safety check: Ensure coords is an array of arrays
    if (!Array.isArray(coords) || coords.length === 0) return GeometryType.Polyline;
    if (!Array.isArray(coords[0])) return GeometryType.Point; // Single point disguised

    // Need at least 3 points to make a polygon
    if (coords.length < 3) return GeometryType.Polyline;
    
    const start = coords[0];
    const end = coords[coords.length - 1];
    
    // Check if start and end are roughly the same (closed loop)
    // Tolerance of 0.0001 degrees is roughly 11 meters
    const isClosed = 
       Math.abs(start[0] - end[0]) < 0.0001 &&
       Math.abs(start[1] - end[1]) < 0.0001;

    return isClosed ? GeometryType.Polygon : GeometryType.Polyline;
};
